import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import { z } from "zod";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";

interface BlockStudentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  studentName: string;
  onConfirm: (reason?: string) => Promise<void>;
}

interface BlockStudentFormData {
  reason: string;
}

const createBlockStudentSchema = (t: any) =>
  z.object({
    reason: z.string().max(255, t("workspaces:settings.members.dialog.reasonTooLong")).optional(),
  });

export const BlockStudentDialog = ({
  isOpen,
  onClose,
  studentName,
  onConfirm,
}: BlockStudentDialogProps) => {
  const { t } = useTranslation(["workspaces", "common"]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonLength, setReasonLength] = useState(0);
  const [hasFutureLessonsError, setHasFutureLessonsError] = useState(false);
  const blockStudentSchema = createBlockStudentSchema(t);

  const form = useForm<BlockStudentFormData>({
    resolver: zodResolver(blockStudentSchema),
    defaultValues: {
      reason: "",
    },
  });

  const getErrorMessage = (errorCode: string, errorMessage?: string) => {
    const errorMap: Record<string, string> = {
      student_has_future_lessons: t(
        "workspaces:settings.members.errors.futureLessons",
        "You cannot block this student because they have reserved future lessons. Please cancel all their scheduled lessons first.",
      ),
      not_workspace_member: t(
        "workspaces:settings.members.errors.notWorkspaceMember",
        "You are not a member of this workspace.",
      ),
      user_not_student_in_workspace: t(
        "workspaces:settings.members.errors.notStudent",
        "The user is not a student in this workspace.",
      ),
      not_authenticated: t(
        "workspaces:settings.members.errors.notAuthenticated",
        "Authentication required",
      ),
      insufficient_permissions: t(
        "workspaces:settings.members.errors.insufficientPermissions",
        "Insufficient permissions",
      ),
    };

    return (
      errorMap[errorCode] ||
      errorMessage ||
      t(
        "workspaces:settings.members.errors.generic",
        "An error occurred while processing your request",
      )
    );
  };

  const handleSubmit = async (data: BlockStudentFormData) => {
    setIsSubmitting(true);
    try {
      // Only include reason in request if it's not empty
      const reason = data.reason?.trim() || undefined;

      await onConfirm(reason);

      form.reset();
      onClose();
    } catch (error: any) {
      console.error("Failed to block student:", error);

      const errorCode = error?.response?.data?.code;
      const errorMessage = error?.response?.data?.message;
      const errorMsg = getErrorMessage(errorCode, errorMessage);

      // Check if it's a future lessons error
      if (errorCode === "student_has_future_lessons") {
        setHasFutureLessonsError(true);
      }

      // Handle validation errors specifically
      if (error?.response?.status === 422 && error?.response?.data?.errors) {
        const validationErrors = error.response.data.errors;

        if (validationErrors.reason) {
          form.setError("reason", {
            type: "server",
            message: validationErrors.reason[0],
          });
        }
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      form.reset();
      setReasonLength(0);
      setHasFutureLessonsError(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-foreground/80 h-fit max-h-dvh max-w-2xl border-none p-0 backdrop-blur-xl">
        <ScrollArea className="h-full max-h-dvh">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="p-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader className="pb-4">
              <DialogTitle className="text-accent-foreground flex items-center gap-3 text-xl font-semibold">
                <div className="bg-muted/20 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm">
                  <Icon
                    className="text-red-500"
                    height="1.25rem"
                    icon="mdi:block-helper"
                    width="1.25rem"
                  />
                </div>
                {t("workspaces:settings.members.dialog.blockTitle", "Block Student")}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              <div>
                <p className="text-muted-foreground mb-4 text-sm">
                  {t("workspaces:settings.members.dialog.description", {
                    defaultValue:
                      "Are you sure you want to block {{studentName}}? This will prevent them from accessing the workspace and all its resources.",
                    studentName,
                  })}
                </p>

                <Form {...form}>
                  <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
                    <FormField
                      control={form.control}
                      name="reason"
                      render={({ field }) => (
                        <div>
                          <FormItem className="space-y-3">
                            <div className="flex items-center justify-between">
                              <FormLabel className="text-accent-foreground flex items-center gap-2 text-lg font-medium">
                                <Icon height={"1.25rem"} icon="mdi:text-box" width={"1.25rem"} />
                                {t(
                                  "workspaces:settings.members.dialog.reasonLabel",
                                  "Reason for blocking",
                                )}
                              </FormLabel>
                              <span className="text-muted-foreground text-sm">
                                {reasonLength}/{255}
                              </span>
                            </div>
                            <FormControl>
                              <Card className="border-input overflow-hidden rounded-xl border shadow-sm">
                                <CardContent className="p-0">
                                  <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-32 w-full rounded-xl">
                                    <Textarea
                                      className="[field-sizing:content] min-h-[128px] resize-none rounded-xl border-none! p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                                      placeholder={t(
                                        "workspaces:settings.members.dialog.reasonPlaceholder",
                                        "Enter reason for blocking this student (optional)",
                                      )}
                                      {...field}
                                      maxLength={255}
                                      onChange={(e) => {
                                        field.onChange(e.target.value);
                                        setReasonLength(e.target.value.length);
                                      }}
                                    />
                                  </ScrollArea>
                                </CardContent>
                              </Card>
                            </FormControl>
                          </FormItem>
                        </div>
                      )}
                    />

                    {/* Action buttons */}
                    <div className="flex justify-end gap-3 pt-4">
                      <Button
                        className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
                        disabled={isSubmitting}
                        type="button"
                        variant="outline"
                        onClick={handleClose}
                      >
                        {t("common:cancel")}
                      </Button>
                      <Button
                        className="gap-2 rounded-xl"
                        disabled={isSubmitting || hasFutureLessonsError}
                        type="submit"
                        variant="destructive"
                      >
                        {isSubmitting ? (
                          <>
                            <Icon className="animate-spin" icon="lucide:loader-2" />
                            {t("workspaces:settings.members.dialog.blocking", "Blocking...")}
                          </>
                        ) : (
                          <>
                            <Icon icon="mdi:block-helper" />
                            {t("workspaces:settings.members.dialog.blockButton", "Block Student")}
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              </div>
            </div>
          </motion.div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
